import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, createFeatureSelector, Store } from '@ngrx/store';
import { catchError, concat, defer, EMPTY, exhaustMap, filter, from, interval, map, merge, mergeMap, Observable, tap } from 'rxjs';
import { UpdateEventNavigatorGQL } from 'src/generated/graphql.generated';

import { generateMutationContext } from '../../../core/auth/session';
import type { JobsState } from '../../../jobs/jobs.reducer';
import { OnlineStatusService } from '../../../online-status.service';
import { quickJsActions } from '../../../quickjs/quickjs.actions';
import { FreyaNotificationsService } from '../../../services/freya-notifications.service';
import { selectUrl } from '../../../state/router';
import { getJobCustomerUser } from '../../../utilities/job-customer.util';
import { WorkOrdersActions } from '../../job-workorders/job-workorders.actions';

import { NavigatorOutboxActions } from './navigator-outbox.actions';
import { navigatorOutboxFeature } from './navigator-outbox.reducer';
import { navigatorReplyActions } from './navigator-outbox.send';
import { InventoryChanges, isNoSignalError } from './navigator-outbox.util';

/** How often waiting saves are retried while the phone reports a connection that is not working. */
const RETRY_WAITING_SAVES_MS = 30_000;

/** How long the message about a refused inventory save for a job not on screen stays up. */
const REFUSED_ELSEWHERE_MESSAGE_MS = 120_000;

/**
 * The jobs slice by name, not through `jobsFeature`: its reducer imports this folder's actions, so
 * importing it here would make a circular import.
 */
const selectJobsSlice = createFeatureSelector<JobsState>('Jobs');

/**
 * Sends the waiting writes, oldest first, from anywhere in the app: when the phone reconnects, when
 * the app loads (`isOnline$` replays the current state on subscribe), as the crew keeps working,
 * and every 30s while any are waiting — a weak signal often never reports itself as offline.
 *
 * Stops at the first write that fails for lack of signal; it stays at the front for the next try. A
 * refused write leaves the outbox with today's error toast and the rest carry on.
 *
 * Sending one twice is harmless: the server merges each write into the stored run (#48), so a
 * replay of a write that did land — its answer lost on the way back — sets the same values again.
 */
export const replayNavigatorOutbox = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  updateEventNavigatorGQL = inject(UpdateEventNavigatorGQL),
  onlineStatus = inject(OnlineStatusService),
) => {
  const selectQueue = store.selectSignal(navigatorOutboxFeature.selectQueue);

  // Reads the outbox afresh for each write, so writes queued while the replay runs are sent too.
  // `sent` rather than "the first entry": the store may not have dropped the last one yet.
  const sendWaiting = (sent: Set<string>): Observable<Action> => defer(() => {
    const queue = selectQueue();
    const entry = queue.find((e) => !sent.has(e.id));
    if (!entry) { return EMPTY; }
    sent.add(entry.id);

    const { eventId } = entry.write;
    const lastForEvent = !queue.some((e) => !sent.has(e.id) && e.write.eventId === eventId);

    return updateEventNavigatorGQL.mutate({ ...entry.write }, { context: generateMutationContext() }).pipe(
      mergeMap((result) => {
        const reply = result.data?.updateEventNavigator;
        const stored = reply?.navigatorData;
        // The server's run once the last of this event's writes is in — not before, or the copy
        // the crew sees would briefly lose the writes still waiting.
        const refresh = lastForEvent && entry.jobId && stored != null
          ? [ WorkOrdersActions.navigatorDataUpdatedLocally({ jobId: entry.jobId, eventId, navigatorData: stored }) ]
          : [];
        return concat(from([
          NavigatorOutboxActions.replaySaveSuccess({ id: entry.id }),
          ...navigatorReplyActions(entry.jobId, eventId, reply),
          ...refresh,
        ]), sendWaiting(sent));
      }),
      catchError((error) => {
        if (isNoSignalError(error)) { return EMPTY; }
        return concat(
          from([
            NavigatorOutboxActions.replaySaveRefused({ id: entry.id, error }),
            ...inventoryRefusedActions(entry.jobId, entry.write.eventId, entry.write.inventoryChanges),
            quickJsActions.syncNavigatorStepError({ error }),
          ]),
          sendWaiting(sent),
        );
      }),
    );
  });

  return merge(
    onlineStatus.isOnline$.pipe(filter(Boolean)),
    actions$.pipe(ofType(NavigatorOutboxActions.saveQueued)),
    interval(RETRY_WAITING_SAVES_MS),
  ).pipe(
    filter(() => navigator.onLine && selectQueue().length > 0),
    exhaustMap(() => sendWaiting(new Set())),
  );
}, { functional: true, dispatch: true });

/** The crew's inventory changes in a refused write, handed back to them (#86c). */
function inventoryRefusedActions(jobId: string | null, eventId: string, inventoryChanges: string | undefined): Action[] {
  if (!inventoryChanges) { return []; }
  try {
    const changes: InventoryChanges = JSON.parse(inventoryChanges);
    return [ NavigatorOutboxActions.inventorySaveRefused({ jobId, eventId, changes }) ];
  } catch {
    return [];
  }
}

/**
 * Puts this device's copy of the run where ops left it when the server dropped part or all of a
 * crew save (#86b), so the workorders row and a return to the navigator agree with the server.
 * Moving a navigator that is open happens in its own reducer; telling the crew, on its page.
 */
export const applyDroppedNavigatorSave = createEffect((
  actions$ = inject(Actions),
) => {
  return actions$.pipe(
    ofType(NavigatorOutboxActions.saveDropped),
    filter(({ jobId }) => !!jobId),
    map(({ jobId, eventId, currentStep, navigatorData }) => WorkOrdersActions.navigatorUpdatedLocally({
      jobId: jobId ?? '',
      eventId,
      navigatorCurrentStep: currentStep,
      navigatorData,
    })),
  );
}, { functional: true, dispatch: true });

/**
 * The server refused the crew's inventory save while this device is not on that job's pages (#86c)
 * — a save that waited for signal while the crew moved on. Nothing can put those edits back there
 * (`restoreRefusedNavigatorInventory` runs on the job's own pages), so the crew is told which job and
 * a person can enter them again. Here rather than with the job's effects, which only run on job
 * pages; up for two minutes, as the crew may be anywhere in the app.
 */
export const reportRefusedInventoryElsewhere = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  notify = inject(FreyaNotificationsService),
) => {
  return actions$.pipe(
    ofType(NavigatorOutboxActions.inventorySaveRefused),
    concatLatestFrom(() => [ store.select(selectUrl), store.select(selectJobsSlice) ]),
    filter(([ { jobId }, url ]) => !!jobId && !(url ?? '').startsWith(`/jobs/${ jobId }`)),
    tap(([ { jobId }, _url, { jobs } ]) => {
      const job = jobs[jobId ?? ''];
      const customer = getJobCustomerUser(job?.users);
      const customerName = [ customer?.givenName, customer?.familyName ].filter(Boolean).join(' ');
      notify.error(
        `Inventory changes for job ${ job?.code ?? jobId }${ customerName ? ` (${ customerName })` : '' } were not saved`,
        'The server refused them, and they could not be kept on this phone. Tell the office what you changed.',
        REFUSED_ELSEWHERE_MESSAGE_MS,
      );
    }),
  );
}, { functional: true, dispatch: false });
